import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Box, Typography, Autocomplete, TextField, Button, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableRow, TableCell,
} from '@mui/material';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import { initializeDB } from '../../config/db';
import { PURCHASE_ORDER_STORE, EQUIPMENT_STORE } from '../../config/constants';
import { mergeDataGridStyles } from '../../styles/dataGridStyles';
import poEditService from '../../services/poEditService';

const RENTAL_COST_CODE_PREFIX = '04-500';
const EDITABLE_FIELDS = ['callOffDate', 'job'];

const tapToAdd = (
    <Typography sx={{ fontSize: '0.75rem', color: '#bdbdbd', fontStyle: 'italic' }}>
        Tap to add
    </Typography>
);

const gridStyles = mergeDataGridStyles({
    '& .MuiDataGrid-columnHeaderTitleContainer': { overflow: 'visible' },
    '& .MuiDataGrid-columnHeaderTitle': {
        overflow: 'visible', textOverflow: 'clip', whiteSpace: 'normal', lineHeight: 1.2, fontWeight: 600,
    },
    '& .MuiDataGrid-menuIcon': { width: 0, minWidth: 0, overflow: 'visible' },
    '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#fafafa' },
    '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#fff' },
    '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04) !important' },
    '& .MuiDataGrid-cell': {
        whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3, display: 'flex', alignItems: 'center',
    },
    '& .MuiDataGrid-cell--editable': {
        backgroundColor: 'rgba(25, 118, 210, 0.06)',
        borderBottom: '2px solid rgba(25, 118, 210, 0.3)',
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.14)' },
    },
    '& .MuiDataGrid-cell--editing': {
        backgroundColor: '#e3f2fd !important',
        boxShadow: 'inset 0 0 0 2px #1976d2',
    },
    '& .MuiDataGrid-cell[data-field="description"], & .MuiDataGrid-cell[data-field="estNote"]': {
        backgroundColor: 'rgba(25, 118, 210, 0.06)',
        borderBottom: '2px solid rgba(25, 118, 210, 0.3)',
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.14)' },
    },
    '& .row-dirty': {
        borderLeft: '3px solid #ff9800',
        backgroundColor: 'rgba(255, 152, 0, 0.06) !important',
    },
    '& .row-locally-edited': {
        borderLeft: '3px solid #1976d2',
        backgroundColor: 'rgba(25, 118, 210, 0.04) !important',
    },
});

const TwoLineHeader = ({ line1, line2 }) => (
    <Box sx={{ lineHeight: 1.2 }}>
        <Box sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{line1}</Box>
        <Box sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary' }}>{line2}</Box>
    </Box>
);

const RentalEquipmentTable = ({ selectedJobCodes, jobs }) => {
    const apiRef = useGridApiRef();
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [equipmentData, setEquipmentData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirtyRows, setDirtyRows] = useState({});
    const dirtyRowsRef = useRef(dirtyRows);
    dirtyRowsRef.current = dirtyRows;

    // Description edit dialog
    const [descDialog, setDescDialog] = useState({ open: false, row: null, text: '' });

    // Service Request dialog
    const [srDialog, setSrDialog] = useState({ open: false, row: null, matchedEquip: null, text: '' });

    const loadData = useCallback(async () => {
        setLoading(true);
        const db = await initializeDB();
        const [allPOs, allEquipment] = await Promise.all([
            db.getAll(PURCHASE_ORDER_STORE),
            db.getAll(EQUIPMENT_STORE),
        ]);
        setPurchaseOrders(allPOs);
        setEquipmentData(allEquipment);
        setLoading(false);
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    useEffect(() => {
        const handler = () => loadData();
        window.addEventListener('poEditReverted', handler);
        return () => window.removeEventListener('poEditReverted', handler);
    }, [loadData]);

    const rows = useMemo(() => {
        if (!selectedJobCodes || selectedJobCodes.length === 0) return [];
        const jobSet = new Set(selectedJobCodes.map(c => c.trim()));
        return purchaseOrders.filter(po =>
            po.status !== false &&
            po.costCode?.trim().startsWith(RENTAL_COST_CODE_PREFIX) &&
            jobSet.has(po.job?.trim())
        );
    }, [selectedJobCodes, purchaseOrders]);

    const jobOptions = useMemo(() => jobs?.map(j => j.Job).filter(Boolean) || [], [jobs]);

    const jobNameMap = useMemo(() => {
        const map = {};
        jobs?.forEach(j => { if (j.Job) map[j.Job] = j.JobName || ''; });
        return map;
    }, [jobs]);

    const dirtyCount = Object.keys(dirtyRows).length;

    // --- Undo a single cell back to its original value ---
    const handleUndoCell = useCallback((rowId, field) => {
        setDirtyRows(prev => {
            const rowDirty = prev[rowId];
            if (!rowDirty) return prev;

            const oldVal = rowDirty.oldValues[field];

            // Revert this field in purchaseOrders
            setPurchaseOrders(p => p.map(po => {
                if (po.id !== rowId) return po;
                const updated = { ...po, [field]: oldVal };
                // Check if any OTHER fields remain dirty
                const remainingChanges = { ...rowDirty.changes };
                delete remainingChanges[field];
                if (Object.keys(remainingChanges).length === 0) {
                    delete updated._dirty;
                }
                return updated;
            }));

            // Remove this field from dirty tracking
            const newChanges = { ...rowDirty.changes };
            const newOldValues = { ...rowDirty.oldValues };
            delete newChanges[field];
            delete newOldValues[field];

            if (Object.keys(newChanges).length === 0) {
                // Row is fully clean now
                const next = { ...prev };
                delete next[rowId];
                return next;
            }
            return { ...prev, [rowId]: { ...rowDirty, changes: newChanges, oldValues: newOldValues } };
        });
    }, []);

    // --- Helper: wrap cell content with undo icon if dirty ---
    const withUndo = useCallback((params, field, content) => {
        const isDirty = !!dirtyRowsRef.current[params.row.id]?.changes?.hasOwnProperty(field);
        if (!isDirty) return content;
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.25 }}>
                <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>{content}</Box>
                <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleUndoCell(params.row.id, field); }}
                    sx={{ p: 0.25, flexShrink: 0 }}
                >
                    <UndoIcon sx={{ fontSize: 14, color: '#ff9800' }} />
                </IconButton>
            </Box>
        );
    }, [handleUndoCell]);

    const columns = useMemo(() => [
        {
            field: 'po',
            headerName: 'PO',
            width: 100,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {params.value || '-'}
                </Typography>
            ),
        },
        {
            field: 'vendor',
            headerName: 'Vendor',
            flex: 0.5,
            minWidth: 80,
            renderCell: (params) => (
                <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word', lineHeight: 1.3, fontSize: '0.8rem' }}>
                    {params.value || '-'}
                </Box>
            ),
        },
        {
            field: 'description',
            headerName: 'Description',
            flex: 0.6,
            minWidth: 90,
            renderCell: (params) => withUndo(params, 'description',
                params.value
                    ? <Box sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: 1.3,
                        fontSize: '0.8rem',
                    }}>{params.value}</Box>
                    : tapToAdd
            ),
        },
        {
            field: 'job',
            headerName: 'Job',
            flex: 0.6,
            minWidth: 95,
            editable: true,
            renderHeader: () => <TwoLineHeader line1="Job" line2="Location" />,
            renderEditCell: (params) => (
                <Autocomplete
                    value={params.value || ''}
                    onChange={(_, newValue) => {
                        params.api.setEditCellValue({ id: params.id, field: params.field, value: newValue || '' });
                        setTimeout(() => params.api.stopCellEditMode({ id: params.id, field: params.field }), 0);
                    }}
                    options={jobOptions}
                    getOptionLabel={(opt) => opt || ''}
                    renderOption={(props, opt) => (
                        <li {...props} key={opt}>
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{opt}</Typography>
                                {jobNameMap[opt] && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        {jobNameMap[opt]}
                                    </Typography>
                                )}
                            </Box>
                        </li>
                    )}
                    openOnFocus
                    autoHighlight
                    size="small"
                    fullWidth
                    slotProps={{ popper: { style: { width: 280 } } }}
                    renderInput={(inputParams) => (
                        <TextField
                            {...inputParams}
                            autoFocus
                            size="small"
                            placeholder="Search job..."
                            variant="standard"
                            sx={{ '& .MuiInputBase-root': { px: 1, height: '100%' } }}
                        />
                    )}
                    sx={{ width: '100%', height: '100%' }}
                />
            ),
            renderCell: (params) => withUndo(params, 'job',
                <Box sx={{ py: 0.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.2 }}>
                        {params.value || '-'}
                    </Typography>
                    {params.row.location && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2, display: 'block' }}>
                            {params.row.location}
                        </Typography>
                    )}
                </Box>
            ),
        },
        {
            field: 'callOffDate',
            headerName: 'Call Off Request',
            flex: 0.6,
            minWidth: 95,
            editable: true,
            type: 'date',
            valueGetter: (value) => {
                if (!value) return null;
                const d = new Date(value);
                return isNaN(d.getTime()) ? null : d;
            },
            valueSetter: (value, row) => {
                if (!value) return { ...row, callOffDate: null };
                const d = new Date(value);
                return { ...row, callOffDate: d.toISOString() };
            },
            renderCell: (params) => withUndo(params, 'callOffDate',
                params.value
                    ? <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{params.value.toLocaleDateString('en-US')}</Typography>
                    : tapToAdd
            ),
        },
        {
            field: 'estNote',
            headerName: 'Service Request',
            flex: 0.6,
            minWidth: 95,
            renderCell: (params) => withUndo(params, 'estNote',
                params.value
                    ? <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word', lineHeight: 1.3, fontSize: '0.8rem' }}>{params.value}</Box>
                    : tapToAdd
            ),
        },
    ], [jobOptions, jobNameMap, withUndo]);

    // Edit cell → only local state, no server call
    const processRowUpdate = useCallback((newRow, oldRow) => {
        const changes = {};
        const oldValues = {};

        for (const field of EDITABLE_FIELDS) {
            const newVal = field === 'callOffDate'
                ? (newRow[field] instanceof Date ? newRow[field].toISOString() : newRow[field])
                : newRow[field];
            const oldVal = field === 'callOffDate'
                ? (oldRow[field] instanceof Date ? oldRow[field].toISOString() : oldRow[field])
                : oldRow[field];

            if (newVal !== oldVal) {
                changes[field] = typeof newVal === 'string' ? newVal : (newVal ?? null);
                oldValues[field] = typeof oldVal === 'string' ? oldVal : (oldVal ?? null);
            }
        }

        if (Object.keys(changes).length === 0) return oldRow;

        // Merge with existing dirty state — keep original oldValues, update changes
        const existing = dirtyRowsRef.current[newRow.id];
        const mergedOldValues = { ...(existing?.oldValues || {}) };
        // Only store the ORIGINAL old value (first time this field was edited)
        for (const f of Object.keys(oldValues)) {
            if (!(f in mergedOldValues)) {
                mergedOldValues[f] = oldValues[f];
            }
        }

        setDirtyRows(prev => ({
            ...prev,
            [newRow.id]: {
                changes: { ...(existing?.changes || {}), ...changes },
                oldValues: mergedOldValues,
                externalId: newRow.externalId,
            },
        }));

        setPurchaseOrders(prev =>
            prev.map(po => po.id === newRow.id ? { ...po, ...changes, _dirty: true } : po)
        );

        return { ...newRow, _dirty: true };
    }, []);

    // Save all dirty rows
    const handleSaveAll = useCallback(async () => {
        setSaving(true);
        const entries = Object.entries(dirtyRows);
        for (const [rowId, { changes, oldValues, externalId }] of entries) {
            await poEditService.saveEdit(Number(rowId), externalId, changes, oldValues);
        }
        setPurchaseOrders(prev =>
            prev.map(po => {
                if (!dirtyRows[po.id]) return po;
                const { _dirty, ...rest } = po;
                return { ...rest, _locallyEdited: true };
            })
        );
        setDirtyRows({});
        setSaving(false);
    }, [dirtyRows]);

    // Open description edit dialog
    const openDescriptionDialog = useCallback((row) => {
        setDescDialog({ open: true, row, text: row.description || '' });
    }, []);

    // Confirm description edit → update dirty rows
    const handleDescConfirm = useCallback(() => {
        const { row, text } = descDialog;
        if (!row) return;

        const oldValue = row.description || null;
        const newValue = text || null;
        if (newValue === oldValue) {
            setDescDialog({ open: false, row: null, text: '' });
            return;
        }

        const existing = dirtyRowsRef.current[row.id];
        const mergedOldValues = { ...(existing?.oldValues || {}) };
        if (!('description' in mergedOldValues)) {
            mergedOldValues.description = oldValue;
        }

        setDirtyRows(prev => ({
            ...prev,
            [row.id]: {
                changes: { ...(existing?.changes || {}), description: newValue },
                oldValues: mergedOldValues,
                externalId: row.externalId,
            },
        }));

        setPurchaseOrders(prev =>
            prev.map(po => po.id === row.id ? { ...po, description: newValue, _dirty: true } : po)
        );

        setDescDialog({ open: false, row: null, text: '' });
    }, [descDialog]);

    // Look up equipment by PO number
    const findEquipment = useCallback((row) => {
        const poNum = (row.po || '').trim();
        if (!poNum) return null;
        return equipmentData.find(eq => {
            const eqPo = (eq.po || '').trim();
            return eqPo && eqPo === poNum;
        }) || null;
    }, [equipmentData]);

    // Open service request dialog
    const openServiceRequestDialog = useCallback((row) => {
        const matched = findEquipment(row);
        setSrDialog({ open: true, row, matchedEquip: matched, text: row.estNote || '' });
    }, [findEquipment]);

    // Confirm service request → update dirty rows
    const handleSrConfirm = useCallback(() => {
        const { row, text } = srDialog;
        if (!row) return;

        const oldValue = row.estNote || null;
        const newValue = text || null;
        if (newValue === oldValue) {
            setSrDialog({ open: false, row: null, matchedEquip: null, text: '' });
            return;
        }

        const existing = dirtyRowsRef.current[row.id];
        const mergedOldValues = { ...(existing?.oldValues || {}) };
        if (!('estNote' in mergedOldValues)) {
            mergedOldValues.estNote = oldValue;
        }

        setDirtyRows(prev => ({
            ...prev,
            [row.id]: {
                changes: { ...(existing?.changes || {}), estNote: newValue },
                oldValues: mergedOldValues,
                externalId: row.externalId,
            },
        }));

        setPurchaseOrders(prev =>
            prev.map(po => po.id === row.id ? { ...po, estNote: newValue, _dirty: true } : po)
        );

        setSrDialog({ open: false, row: null, matchedEquip: null, text: '' });
    }, [srDialog]);

    // Single tap to edit (iPad needs this — default is double-click)
    const handleCellClick = useCallback((params) => {
        if (params.field === 'description') {
            openDescriptionDialog(params.row);
            return;
        }
        if (params.field === 'estNote') {
            openServiceRequestDialog(params.row);
            return;
        }
        if (params.isEditable && apiRef.current.getCellMode(params.id, params.field) === 'view') {
            apiRef.current.startCellEditMode({ id: params.id, field: params.field });
        }
    }, [apiRef, openDescriptionDialog, openServiceRequestDialog]);

    const handleCellEditStop = useCallback((params, event) => {
        if (params.field === 'job' && params.reason === 'cellFocusOut') {
            // Only prevent close if focus went to the Autocomplete popper (portal)
            const related = event?.relatedTarget;
            if (related?.closest?.('.MuiAutocomplete-popper')) {
                event.defaultMuiPrevented = true;
            }
        }
    }, []);

    const handleProcessRowUpdateError = useCallback((error) => {
        console.error('[RentalEquipmentTable] Row update error:', error);
    }, []);

    const getRowClassName = useCallback((params) => {
        if (params.row._dirty) return 'row-dirty';
        if (params.row._locallyEdited) return 'row-locally-edited';
        return '';
    }, []);

    return (
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', px: 1, pb: 1 }}>
            {rows.length === 0 && !loading ? (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        No rental equipment found ({selectedJobCodes.length} job{selectedJobCodes.length !== 1 ? 's' : ''} selected)
                    </Typography>
                </Box>
            ) : (
                <>
                    <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
                        <DataGrid
                            apiRef={apiRef}
                            rows={rows}
                            columns={columns}
                            loading={loading}
                            getRowId={(row) => row.id}
                            processRowUpdate={processRowUpdate}
                            onProcessRowUpdateError={handleProcessRowUpdateError}
                            onCellClick={handleCellClick}
                            onCellEditStop={handleCellEditStop}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 25 } },
                            }}
                            pageSizeOptions={[25, 50, 100]}
                            disableRowSelectionOnClick
                            columnHeaderHeight={52}
                            rowHeight={52}
                            getRowClassName={getRowClassName}
                            sx={gridStyles}
                        />
                    </Box>

                    {dirtyCount > 0 && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 1.5,
                            py: 0.75,
                            backgroundColor: '#fff3e0',
                            borderTop: '2px solid #ff9800',
                            borderRadius: '0 0 4px 4px',
                            flexShrink: 0,
                        }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#e65100' }}>
                                {dirtyCount} unsaved change{dirtyCount !== 1 ? 's' : ''}
                            </Typography>
                            <Button
                                size="small"
                                variant="contained"
                                color="warning"
                                startIcon={<SaveIcon />}
                                onClick={handleSaveAll}
                                disabled={saving}
                                sx={{ ml: 'auto', fontSize: '0.75rem', py: 0.25 }}
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </Button>
                        </Box>
                    )}
                </>
            )}

            {/* Description Edit Dialog */}
            <Dialog
                open={descDialog.open}
                onClose={() => setDescDialog({ open: false, row: null, text: '' })}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
                    Edit Description — PO# {descDialog.row?.po || 'N/A'}
                </DialogTitle>
                <DialogContent>
                    <Table size="small" sx={{ mb: 2, '& td': { py: 0.5, fontSize: '0.85rem', border: 'none' } }}>
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: 120 }}>Vendor</TableCell>
                                <TableCell>{descDialog.row?.vendor || '—'}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Equipment</TableCell>
                                <TableCell>{descDialog.row?.equipment || '—'}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>

                    <TextField
                        label="Description"
                        multiline
                        rows={4}
                        fullWidth
                        value={descDialog.text}
                        onChange={(e) => {
                            if (e.target.value.length <= 255) {
                                setDescDialog(prev => ({ ...prev, text: e.target.value }));
                            }
                        }}
                        placeholder="Enter description..."
                        autoFocus
                        inputProps={{ maxLength: 255 }}
                        helperText={`${descDialog.text.length}/255`}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDescDialog({ open: false, row: null, text: '' })}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleDescConfirm}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Service Request Dialog */}
            <Dialog
                open={srDialog.open}
                onClose={() => setSrDialog({ open: false, row: null, matchedEquip: null, text: '' })}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
                    Service Request — PO# {srDialog.row?.po || 'N/A'}
                </DialogTitle>
                <DialogContent>
                    {/* PO Info */}
                    <Table size="small" sx={{ mb: 2, '& td': { py: 0.5, fontSize: '0.85rem', border: 'none' } }}>
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: 120 }}>Vendor</TableCell>
                                <TableCell>{srDialog.row?.vendor || '—'}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>

                    {/* Equipment match info */}
                    {srDialog.matchedEquip ? (
                        <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#e8f5e9', borderRadius: 1, border: '1px solid #a5d6a7' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 0.5 }}>
                                Equipment Found — {srDialog.matchedEquip.equipment}
                            </Typography>
                            <Table size="small" sx={{ '& td': { py: 0.25, fontSize: '0.8rem', border: 'none' } }}>
                                <TableBody>
                                    {srDialog.matchedEquip.description && (
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600, width: 100 }}>Description</TableCell>
                                            <TableCell>{srDialog.matchedEquip.description}</TableCell>
                                        </TableRow>
                                    )}
                                    {srDialog.matchedEquip.make && (
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Make</TableCell>
                                            <TableCell>{srDialog.matchedEquip.make}</TableCell>
                                        </TableRow>
                                    )}
                                    {srDialog.matchedEquip.model && (
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Model</TableCell>
                                            <TableCell>{srDialog.matchedEquip.model}</TableCell>
                                        </TableRow>
                                    )}
                                    {srDialog.matchedEquip.serialNo && (
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Serial #</TableCell>
                                            <TableCell>{srDialog.matchedEquip.serialNo}</TableCell>
                                        </TableRow>
                                    )}
                                    {srDialog.matchedEquip.category && (
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                                            <TableCell>{srDialog.matchedEquip.category}</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    ) : (
                        <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc80' }}>
                            <Typography variant="body2" sx={{ color: '#e65100' }}>
                                Verify equipment identifiers before submitting.
                            </Typography>
                        </Box>
                    )}

                    {/* Service request text input */}
                    <TextField
                        label="Service Request"
                        multiline
                        rows={3}
                        fullWidth
                        value={srDialog.text}
                        onChange={(e) => setSrDialog(prev => ({ ...prev, text: e.target.value }))}
                        placeholder="Describe the service needed..."
                        autoFocus
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setSrDialog({ open: false, row: null, matchedEquip: null, text: '' })}
                    >
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSrConfirm}>
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RentalEquipmentTable;
